import React, { useState, useEffect } from 'react';
import { Save, Eye, Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PoliciesEditorPage = () => {
  const [privacy, setPrivacy] = useState({ content: '', title: '' });
  const [terms, setTerms] = useState({ content: '', title: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy');

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [privacyRes, termsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/policies/privacy`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/policies/terms`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (privacyRes.ok) {
        const data = await privacyRes.json();
        setPrivacy({ content: data.content, title: data.title });
      }

      if (termsRes.ok) {
        const data = await termsRes.json();
        setTerms({ content: data.content, title: data.title });
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePrivacy = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/policies/privacy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: privacy.content })
      });

      if (response.ok) {
        toast.success('✅ Datenschutzerklärung gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const saveTerms = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/policies/terms`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: terms.content })
      });

      if (response.ok) {
        toast.success('✅ AGB gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl">
      {/* Zurück Button */}
      <Button
        variant="outline"
        onClick={() => window.history.back()}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Zurück
      </Button>
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Datenschutz & AGB
        </h1>
        <p className="text-gray-600 mt-1">
          Verwalten Sie Ihre rechtlichen Dokumente
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="privacy">🔒 Datenschutz</TabsTrigger>
          <TabsTrigger value="terms">📜 AGB</TabsTrigger>
        </TabsList>

        {/* Datenschutz */}
        <TabsContent value="privacy">
          <Card>
            <CardHeader>
              <CardTitle>Datenschutzerklärung bearbeiten</CardTitle>
              <CardDescription>
                Diese Datenschutzerklärung wird Kunden bei der Registrierung angezeigt
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={privacy.content}
                onChange={(e) => setPrivacy({ ...privacy, content: e.target.value })}
                rows={20}
                className="font-mono text-sm"
              />
              
              <div className="flex gap-3">
                <Button onClick={savePrivacy} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Speichern...' : 'Speichern'}
                </Button>
                <Button variant="outline" onClick={() => window.open('/verify-test', '_blank')}>
                  <Eye className="h-4 w-4 mr-2" />
                  Vorschau (Registrierung)
                </Button>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-900">
                <p><strong>ℹ️ Hinweis:</strong> Kunden müssen bei der Registrierung die Datenschutzerklärung akzeptieren</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AGB */}
        <TabsContent value="terms">
          <Card>
            <CardHeader>
              <CardTitle>Allgemeine Geschäftsbedingungen bearbeiten</CardTitle>
              <CardDescription>
                Diese AGB werden Kunden bei der Registrierung angezeigt
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={terms.content}
                onChange={(e) => setTerms({ ...terms, content: e.target.value })}
                rows={20}
                className="font-mono text-sm"
              />
              
              <div className="flex gap-3">
                <Button onClick={saveTerms} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Speichern...' : 'Speichern'}
                </Button>
                <Button variant="outline" onClick={() => window.open('/verify-test', '_blank')}>
                  <Eye className="h-4 w-4 mr-2" />
                  Vorschau (Registrierung)
                </Button>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-900">
                <p><strong>ℹ️ Hinweis:</strong> Kunden müssen bei der Registrierung die AGB akzeptieren</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PoliciesEditorPage;
